/**
 * PlayEngine3D games: what the registry holds and what the dashboard has
 * imported from it (superAdmin only).
 *
 * A game reaches the registry with `pe3d publish` on the machine that makes
 * it. Every game is a package of the one scope (`@pe3d-games`), so nothing is
 * asked: the list is the registry's own, joined with what is imported, and a
 * superAdmin picks a game and imports a version — no deploy. An imported game
 * is there for every company that reaches a pe3d base; who reaches a base is
 * the base grant's business (server: controller/pe3d/access.js).
 *
 * The search box is also the look-up: when the registry token cannot list the
 * project (it needs read_api), or a game is not in the listing, typing its name
 * and pressing Enter asks the registry for that one package.
 *
 * Variants live on a pe3d base, a runtime base like the playEngine ones; it can
 * be made from here too ("+ New PE3D base") and is then granted to companies on
 * the Companies view as usual.
 *
 * Same frame as Companies & bases and Runtime bases (settingsPalette.js).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import RefreshIcon from "@material-ui/icons/Refresh";

import DialogBaseCreate from "./dialogBaseCreate";
import { ACCENT, BORDER, INSET_BG, MUTED, SEARCH_INPUT_PROPS, adminScreenFrame } from "./settingsPalette";
import { importRelease, listAdminGames, listRegistryPackages, listRegistryVersions } from "../../modules/pe3dApi";
import { PE3D_PACKAGE_SCOPE, catalogRows, filterCatalogRows, packageOfQuery, releaseRows } from "../../modules/pe3dCatalog";

const useStyles = makeStyles(() => ({
  ...adminScreenFrame,

  // List column
  listHeaderRow: { display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: "24px" },
  refreshButton: { padding: "2px", color: MUTED },
  rowText: { flex: 1, minWidth: 0 },
  listEmpty: { padding: "16px 10px", fontSize: "0.8rem", color: MUTED },
  // The search doubling as a look-up: offered only when nothing listed has that name.
  lookUpRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 10px",
    borderRadius: "3px",
    fontSize: "0.75rem",
    color: ACCENT,
    cursor: "pointer",
    wordBreak: "break-all",
    "&:hover": { backgroundColor: "#454545" },
  },
  listNote: { padding: "0.5rem 0.75rem", borderTop: `1px solid ${BORDER}`, fontSize: "0.7rem", lineHeight: 1.4, color: MUTED, flexShrink: 0 },

  // Detail pane
  detailSub: { padding: "2px 1.25rem 0 1.25rem", fontSize: "0.72rem", color: MUTED, flexShrink: 0 },
  loadingRow: { display: "flex", alignItems: "center", gap: "8px", fontSize: "0.75rem", color: MUTED, marginBottom: "0.75rem" },
  // Recessed, like the checklists: the versions are a list to pick from.
  releaseList: { border: `1px solid ${BORDER}`, borderRadius: "4px", backgroundColor: INSET_BG, overflowY: "auto", minHeight: 0 },
  releaseRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 8px 8px 12px",
    borderBottom: `1px solid ${BORDER}`,
    "&:last-child": { borderBottom: "none" },
  },
  releaseVersion: { display: "flex", alignItems: "center", fontSize: "0.88rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" },
  releaseMeta: { fontSize: "0.7rem", color: MUTED, marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  // One width for both labels, so the buttons line up down the list.
  releaseAction: { flexShrink: 0, minWidth: "92px" },
}));

const errorOf = (result) => {
  const error = result && result.error;
  if (!error) return `HTTP ${result && result.status}`;
  if (typeof error === "string") return error;
  return error.detail || error.error || JSON.stringify(error);
};

const dayOf = (date) => new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
const countOf = (count, one, many) => `${count} ${count === 1 ? one : many}`;

export default function Pe3dGamesPanel({ createSnackbar, headerAction, onBaseCreated }) {
  const classes = useStyles();
  const [games, setGames] = useState(null);
  // What the registry project holds; null until first read, false when the token cannot list.
  const [registryPackages, setRegistryPackages] = useState(null);
  const [isRegistryLoading, setIsRegistryLoading] = useState(true);
  // Packages found by name that the listing did not give: [{ name, latest, newVersions }].
  const [lookedUp, setLookedUp] = useState([]);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  // The selected game's versions in the registry: { package, versions | null, error | null }, null while loading.
  const [registry, setRegistry] = useState(null);
  const registryRequest = useRef(0);
  const [importingVersion, setImportingVersion] = useState(null);
  const [isBaseDialogOpen, setIsBaseDialogOpen] = useState(false);

  const reload = async () => {
    const result = await listAdminGames();
    if (!result.ok) {
      createSnackbar(`Could not load the PlayEngine3D games: ${errorOf(result)}`, "error", false);
      setGames([]);
      return;
    }
    setGames(result.data.games || []);
  };

  const reloadRegistry = async () => {
    setIsRegistryLoading(true);
    const result = await listRegistryPackages();
    setRegistryPackages(result.ok ? result.data.packages || [] : false);
    setIsRegistryLoading(false);
  };

  /** `quiet` keeps the versions on screen while they are read again. */
  const loadVersions = async (packageName, quiet = false) => {
    const request = ++registryRequest.current;
    if (!quiet) setRegistry(null);
    const result = await listRegistryVersions(packageName);
    // Another game was picked while this one was being read.
    if (request !== registryRequest.current) return;
    setRegistry({ package: packageName, versions: result.ok ? result.data.versions : null, error: result.ok ? null : errorOf(result) });
    if (result.ok) {
      const versions = result.data.versions;
      setLookedUp((current) =>
        current.map((item) =>
          item.name === packageName
            ? { name: packageName, latest: versions.length ? versions[0].version : null, newVersions: versions.filter((version) => !version.imported).length }
            : item
        )
      );
    }
  };

  useEffect(() => {
    reload();
    reloadRegistry();
  }, []);

  const rows = useMemo(() => catalogRows(games, registryPackages, lookedUp), [games, registryPackages, lookedUp]);
  const visibleRows = useMemo(() => filterCatalogRows(rows, query), [rows, query]);
  const selected = rows.find((row) => row.package === selectedPackage) || null;
  const selectedGame = selected ? selected.game : null;

  // Something is always open once there is something to open.
  useEffect(() => {
    if (!selected && rows.length) setSelectedPackage(rows[0].package);
  }, [rows, selectedPackage]);

  useEffect(() => {
    if (selectedPackage) loadVersions(selectedPackage);
  }, [selectedPackage]);

  // The name in the search box, when no listed game already has it.
  const queryPackage = packageOfQuery(query);
  const lookUpPackage = queryPackage && !rows.some((row) => row.package === queryPackage) ? queryPackage : null;

  const lookUp = async () => {
    if (!queryPackage || isLookingUp) return;
    if (!lookUpPackage) {
      setSelectedPackage(queryPackage);
      setQuery("");
      return;
    }
    setIsLookingUp(true);
    const result = await listRegistryVersions(lookUpPackage);
    setIsLookingUp(false);
    if (!result.ok) {
      const reason = result.status === 404 ? `${lookUpPackage} is not in the registry.` : `Registry: ${errorOf(result)}`;
      createSnackbar(reason, "error", false);
      return;
    }
    const versions = result.data.versions;
    setLookedUp((current) => [
      ...current.filter((item) => item.name !== lookUpPackage),
      { name: lookUpPackage, latest: versions.length ? versions[0].version : null, newVersions: versions.filter((version) => !version.imported).length },
    ]);
    setQuery("");
    setSelectedPackage(lookUpPackage);
  };

  const runImport = async (version) => {
    const packageName = selected.package;
    setImportingVersion(version);
    const result = await importRelease(packageName, version);
    if (!result.ok) {
      setImportingVersion(null);
      createSnackbar(`Import failed: ${errorOf(result)}`, "error", false);
      return;
    }
    createSnackbar(`${packageName}@${version} imported.`, "success", false);
    await Promise.all([reload(), reloadRegistry(), loadVersions(packageName, true)]);
    setImportingVersion(null);
  };

  if (games === null) {
    return (
      <div className={classes.root}>
        <div className={classes.centred}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  const isRegistryOfSelected = registry && selected && registry.package === selected.package;
  const versions = isRegistryOfSelected ? releaseRows(registry.versions, selectedGame ? selectedGame.releases : []) : [];

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>PlayEngine3D games</Typography>
        <div className={classes.headerSpacer} />
        {headerAction}
        <Button variant="contained" color="primary" onClick={() => setIsBaseDialogOpen(true)}>
          + New PE3D base
        </Button>
      </div>

      <DialogBaseCreate
        isOpen={isBaseDialogOpen}
        initialKind="pe3d"
        onClose={() => setIsBaseDialogOpen(false)}
        onCreated={() => {
          setIsBaseDialogOpen(false);
          if (onBaseCreated) onBaseCreated();
        }}
        createSnackbar={createSnackbar}
      />

      <div className={classes.body}>
        <div className={classes.listColumn}>
          <div className={classes.listHeader}>
            <div className={classes.listHeaderRow}>
              <div className={classes.listLabel}>Games ({rows.length})</div>
              {isRegistryLoading ? (
                <CircularProgress size={14} />
              ) : (
                <Tooltip title="Read the registry again">
                  <IconButton className={classes.refreshButton} onClick={reloadRegistry}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <TextField
              fullWidth
              size="small"
              placeholder="Search, or type a game's name"
              InputProps={SEARCH_INPUT_PROPS}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") lookUp();
              }}
            />
          </div>
          <div className={classes.listScroller}>
            {visibleRows.map((row) => (
              <div
                key={row.package}
                className={`${classes.listRow} ${row.package === selectedPackage ? classes.listRowSelected : ""}`}
                onClick={() => setSelectedPackage(row.package)}
              >
                <div className={classes.rowText}>
                  <div className={classes.rowTitle}>
                    {row.title}
                    {/* The badge language of the other lists: filled for a game
                        that is not here at all, outlined for versions to fetch. */}
                    {!row.game && <span className={`${classes.chip} ${classes.chipFilled}`}>not imported</span>}
                    {row.game && row.newVersions > 0 && <span className={`${classes.chip} ${classes.chipOutline}`}>{row.newVersions} new</span>}
                  </div>
                  <div className={classes.rowMeta}>
                    {row.game
                      ? `${countOf(row.game.releases.length, "release", "releases")}${row.latest ? ` · latest ${row.latest}` : ""}`
                      : row.latest
                        ? `latest ${row.latest}`
                        : "no versions"}
                  </div>
                </div>
              </div>
            ))}
            {visibleRows.length === 0 && !lookUpPackage && (
              <div className={classes.listEmpty}>
                {rows.length > 0 ? "No game matched." : isRegistryLoading ? "Reading the registry…" : "Nothing published yet."}
              </div>
            )}
            {lookUpPackage && (
              <div className={classes.lookUpRow} onClick={lookUp}>
                {isLookingUp && <CircularProgress size={12} />}
                <span>Look up {lookUpPackage} in the registry</span>
              </div>
            )}
          </div>
          {registryPackages === false && (
            <div className={classes.listNote}>
              The registry token cannot list the games (it needs read_api), so only imported ones show. Type a game&apos;s name and press
              Enter to look it up.
            </div>
          )}
        </div>

        <div className={classes.detailColumn}>
          {!selected && <div className={classes.detailEmpty}>{rows.length ? "Select a game." : "No games yet."}</div>}

          {selected && (
            <>
              <Typography className={classes.detailTitle}>{selected.title}</Typography>
              <div className={classes.detailSub}>
                {PE3D_PACKAGE_SCOPE}/{selected.name}
              </div>

              <div className={classes.detailRule} />
              <div className={classes.tabBody}>
                <Typography className={classes.subTitle}>{isRegistryOfSelected ? `Releases · ${versions.length}` : "Releases"}</Typography>
                <Typography className={classes.hint}>
                  {selectedGame
                    ? "Importing downloads a release from the registry into this dashboard; nothing needs a deploy. A variant keeps the release it was made on, so earlier ones stay in use."
                    : "Not imported yet. Importing a release adds the game here, for every company that has a PE3D base."}
                </Typography>
                {!isRegistryOfSelected && (
                  <div className={classes.loadingRow}>
                    <CircularProgress size={14} />
                    Reading the registry…
                  </div>
                )}
                {isRegistryOfSelected && registry.error && (
                  <Typography className={classes.hint}>The registry could not be read ({registry.error}); these are the imported releases.</Typography>
                )}
                {isRegistryOfSelected && versions.length === 0 && !registry.error && <Typography className={classes.hint}>No versions published.</Typography>}
                {versions.length > 0 && (
                  <div className={classes.releaseList}>
                    {versions.map((item) => {
                      const { release } = item;
                      const facts = [
                        item.publishedAt ? `published ${dayOf(item.publishedAt)}` : null,
                        release && release.importedAt ? `imported ${dayOf(release.importedAt)}` : null,
                        item.inRegistry ? null : "no longer in the registry",
                      ].filter(Boolean);
                      const contents = release
                        ? [
                          release.engineVersion ? `engine ${release.engineVersion}` : null,
                          countOf((release.networks || []).length, "network", "networks"),
                          (release.variants || []).length ? `variants: ${release.variants.join(", ")}` : "no variants",
                        ].filter(Boolean)
                        : [];
                      return (
                        <div key={item.version} className={classes.releaseRow}>
                          <div className={classes.rowText}>
                            <div className={classes.releaseVersion}>
                              {item.version}
                              {release && <span className={`${classes.chip} ${classes.chipOutline}`}>imported</span>}
                            </div>
                            {facts.length > 0 && <div className={classes.releaseMeta}>{facts.join(" · ")}</div>}
                            {contents.length > 0 && (
                              <div className={classes.releaseMeta} title={contents.join(" · ")}>
                                {contents.join(" · ")}
                              </div>
                            )}
                          </div>
                          {item.inRegistry && !registry.error && (
                            <Button
                              className={classes.releaseAction}
                              size="small"
                              color={release ? "default" : "primary"}
                              variant={release ? "text" : "outlined"}
                              disabled={importingVersion !== null}
                              onClick={() => runImport(item.version)}
                            >
                              {importingVersion === item.version ? <CircularProgress size={16} /> : release ? "Re-import" : "Import"}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className={classes.footer}>
                <Button variant="outlined" disabled={!isRegistryOfSelected || importingVersion !== null} onClick={() => loadVersions(selected.package)}>
                  Check for new versions
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
